import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import useAuthUser from "../hooks/useAuthUser";
import useUserRole from "../hooks/useUserRole";
import { signOut } from "firebase/auth";
import { auth } from "../firebase";
import { FaUserCircle, FaSearch, FaWhatsapp, FaUserShield, FaUserFriends } from "react-icons/fa";
import { FiMenu, FiX, FiHome } from "react-icons/fi";
import LogoWithSlogan from "./LogoWithSlogan";
import { normalizeRole } from "../utils/roleUtils";
import ReferralModal from './ReferralModal';
import SignupModal from './SignupModal';
import PropTypes from 'prop-types';

export default function Header({ adminMode = false }) {
  const [scrolled, setScrolled] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [showSearchDropdown, setShowSearchDropdown] = useState(false);
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [showMobileNav, setShowMobileNav] = useState(false);
  const [referralModal, setReferralModal] = useState({ open: false, role: null });
  const [showSignup, setShowSignup] = useState(false);
  const [signupRedirect, setSignupRedirect] = useState('/');

  // Popular searches and quick actions
  const popularSearches = [
    { text: 'Speech Therapy', type: 'therapist' },
    { text: 'ABA Therapy', type: 'therapist' },
    { text: 'Occupational Therapy', type: 'therapist' },
    { text: 'Special Education', type: 'resource' },
    { text: 'Autism Resources', type: 'resource' },
  ];
  
  // Get user from auth hook
  const user = useAuthUser();
  const { role: userRole, loading: roleLoading } = useUserRole(user?.uid);
  const navigate = useNavigate();

  // Handle signup success
  const handleSignupSuccess = useCallback(() => {
    setShowSignup(false);
    if (signupRedirect) {
      navigate(signupRedirect);
    }
  }, [navigate, signupRedirect]);

  // Expose the signup modal function globally
  useEffect(() => {
    window.showSignupModal = (redirectUrl = '/') => {
      console.log('showSignupModal called with redirect:', redirectUrl);
      setSignupRedirect(redirectUrl);
      setShowSignup(true);
    };

    // Clean up the global function when the component unmounts
    return () => {
      delete window.showSignupModal;
    };
  }, [navigate]);

  // Navigation items - includes Assessments for logged-in users
  const navItems = [
    { name: 'Home', path: '/' },
    { name: 'About', path: '/about' },
    { name: 'Services', path: '/services' },
    { name: 'Resources', path: '/resources' },
    { name: 'Contact', path: '/contact' },
    ...(user ? [
      { 
        name: 'Assessments', 
        path: '/app/report',
        className: 'font-semibold text-blue-600 hover:text-blue-700' 
      }
    ] : []),
  ];

  const quickActions = [
    { 
      text: 'Take M-CHAT Assessment', 
      icon: 'clipboard-check', 
      type: 'mchat',
      action: (e) => {
        e.preventDefault();
        navigate('/free-assessment');
      }
    },
    { 
      text: 'Find Therapists Near Me', 
      icon: 'map-marker-alt', 
      type: 'therapist', 
      action: (e) => {
        e.preventDefault();
        if (!user) {
          handleLoginClick();
        } else {
          navigate('/search?type=therapist&near_me=true');
        }
      }
    },
    { 
      text: 'Book a Free Consultation', 
      icon: 'calendar-alt', 
      type: 'consultation',
      action: (e) => {
        e.preventDefault();
        if (!user) {
          handleLoginClick();
        } else {
          navigate('/book-consultation');
        }
      }
    },
    { 
      text: 'Browse Resources', 
      icon: 'book', 
      type: 'resource',
      action: (e) => {
        e.preventDefault();
        if (!user) {
          handleLoginClick();
        } else {
          navigate('/resources');
        }
      }
    }
  ];
  // Handle loading state
  const loading = user === undefined || (user && roleLoading);

  // If in admin mode, show admin header
  if (adminMode) {
    return (
      <header className="sticky top-0 z-40 w-full bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 shadow-md backdrop-blur-md">
        <div className="container mx-auto px-4 py-2 flex justify-between items-center">
          <div className="flex items-center space-x-2">
            <FaUserShield className="text-blue-600 text-xl" />
            <span className="font-semibold text-gray-800 dark:text-white">Admin Panel</span>
          </div>
          <div className="flex items-center space-x-4">
            <button 
              onClick={() => navigate('/app/dashboard/parent')}
              className="flex items-center space-x-1 text-sm text-gray-700 dark:text-gray-200 hover:text-blue-600 dark:hover:text-blue-400"
              title="Back to Dashboard"
            >
              <FiHome className="text-lg" />
              <span>App Home</span>
            </button>
            <div className="h-6 border-l border-gray-300 dark:border-gray-600"></div>
            <button 
              onClick={() => signOut(auth).then(() => navigate('/'))}
              className="text-sm text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
            >
              Sign Out
            </button>
          </div>
        </div>
      </header>
    );
  }

  // State management
  const [showLoginModal, setShowLoginModal] = useState(false);

  // Login handling
  const handleOpenLoginModal = () => {
    setShowLoginModal(true);
    if (showMobileNav) {
      setShowMobileNav(false);
    }
  };

  const handleLoginClick = () => {
    window.showSignupModal?.();
    if (showMobileNav) setShowMobileNav(false);
  };

  function normalizeRole(role, email) {
    // Always return 'admin' for asdfloteam@gmail.com
    if (email === 'asdfloteam@gmail.com') return 'admin';
    
    if (!role) return '';
    const r = role.trim().toLowerCase().replace(/\s+/g, '_');
    if (r === 'admin') return 'admin';
    if (["therapist","shadow_teacher","special_educator","psychologist","doctor"].includes(r)) return 'professional';
    if (["parent"].includes(r)) return 'parent';
    if (["center","clinic","cdc","school","inclusiveschool","hospital"].includes(r)) return 'organization';
    return r;
  }

  // Handle user sign out
  const handleSignOut = async () => {
    try {
      await signOut(auth);
      navigate('/');
      window.location.reload(); // Ensure clean state after sign out
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  // Navigation handlers
  const handleDashboardNav = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    console.log('handleDashboardNav called with:', { userRole, user });
    const normRole = normalizeRole(userRole, user?.email);
    console.log('Normalized role:', normRole);
    
    if (normRole === 'admin') {
      console.log('Navigating to /admin');
      navigate('/admin');
    } else if (normRole === 'parent') {
      console.log('Navigating to /app/dashboard/parent');
      navigate('/app/dashboard/parent');
    } else if (normRole === 'professional') {
      console.log('Navigating to /app/dashboard/professional');
      navigate('/app/dashboard/professional');
    } else if (normRole === 'organization') {
      console.log('Navigating to /app/dashboard/organization');
      navigate('/app/dashboard/organization');
    } else {
      console.warn('Unknown role, navigating to /app');
      navigate('/app');
    }
    setShowMobileNav(false);
  };

  const handleProfileNav = () => {
    const normRole = normalizeRole(userRole);
    if (normRole === 'parent') navigate('/app/profile/parent');
    else if (normRole === 'professional') navigate('/app/profile/professional');
    else if (normRole === 'organization') navigate('/app/profile/organization');
    else navigate('/app/profile');
    setShowMobileNav(false);
  };

  // Handle search submission
  const handleSearch = (e, query = searchQuery, type = 'all') => {
    e.preventDefault();
    const searchTerm = query.trim();
    if (searchTerm) {
      const searchUrl = type === 'all' 
        ? `/search?q=${encodeURIComponent(searchTerm)}`
        : `/search?q=${encodeURIComponent(searchTerm)}&type=${type}`;
      navigate(searchUrl);
      setSearchQuery('');
      setShowSearchDropdown(false);
      setShowMobileNav(false);
    }
  };

  // Handle search focus
  const handleSearchFocus = () => {
    setIsSearchFocused(true);
    setShowSearchDropdown(true);
  };

  // Handle search blur with delay to allow clicks on dropdown items
  const handleSearchBlur = () => {
    setTimeout(() => {
      setIsSearchFocused(false);
      setShowSearchDropdown(false);
    }, 200);
  };

  return (
    <>



      {/* Slim Referral Banner */}
      <div className="w-full bg-gradient-to-r from-indigo-600 via-blue-600 to-sky-600 text-white text-xs sm:text-sm py-1.5 text-center shadow-sm">
        <button
          type="button"
          onClick={() => setReferralModal({ open: true, role: null })}
          className="inline-flex items-center gap-2 hover:underline underline-offset-2 transition-colors"
          aria-label="Refer someone to asdflo"
        >
          <span className="font-normal">
            Know a parent, therapist, school or CDC? Refer them to asdflo.
          </span>
        </button>
      </div>

      {/* Referral Modal */}
      <ReferralModal 
        open={referralModal.open} 
        onClose={() => setReferralModal({ open: false, role: null })} 
        defaultRole={referralModal.role} 
      />

      {/* Main Header */}
      <header className={`sticky top-0 z-[900] w-full bg-white/95 backdrop-blur-md border-b border-gray-100 shadow-sm transition-all duration-300 ${scrolled ? 'shadow-md' : 'shadow-sm'}`} style={{ position: 'relative' }}>
        <div className="container mx-auto px-3 sm:px-4 md:px-8 flex items-center justify-between h-14 sm:h-16 md:h-20">
          {/* Logo with Slogan - Hidden on mobile, shown in mobile header */}
          <a href="/" className="hidden md:flex items-center no-underline hover:no-underline">
            <LogoWithSlogan />
          </a>

          {/* Search Bar - Center */}
          <div className="hidden lg:flex flex-1 max-w-xl mx-8">
            <form onSubmit={handleSearch} className="relative w-full">
              <div className="relative w-full">
                <input
                  type="text"
                  placeholder="Find therapists, resources, or support..."
                  className="w-full px-5 pr-12 py-2.5 text-sm rounded-full border border-gray-200 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onFocus={handleSearchFocus}
                  onBlur={handleSearchBlur}
                />
                {(showSearchDropdown && isSearchFocused) && (
                  <div className="absolute z-50 mt-1 w-full bg-white rounded-xl shadow-xl border border-gray-100 overflow-hidden">
                    {/* Quick Actions */}
                    <div className="p-3 border-b border-gray-100">
                      <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 px-2">Quick Actions</h3>
                      <div className="grid grid-cols-1 gap-1">
                        {quickActions.map((action, index) => (
                          <button
                            key={index}
                            onClick={action.action || ((e) => handleSearch(e, action.text, action.type))}
                            className="flex items-center w-full px-4 py-2.5 text-sm text-gray-700 hover:bg-blue-50 rounded-lg transition-colors text-left"
                          >
                            <i className={`fas fa-${action.icon} ${action.type === 'mchat' ? 'text-green-500' : 'text-blue-500'} w-5 mr-3 text-center`}></i>
                            <span>{action.text}</span>
                            {action.location && !user && (
                              <span className="ml-auto text-xs text-blue-500 bg-blue-50 px-2 py-0.5 rounded-full">
                                <i className="fas fa-lock mr-1"></i>
                                Sign In
                              </span>
                            )}
                            {action.location && user && (
                              <span className="ml-auto text-xs text-blue-500 bg-blue-50 px-2 py-0.5 rounded-full">
                                <i className="fas fa-location-arrow mr-1"></i>
                                Detect Location
                              </span>
                            )}
                            {!action.location && !user && action.type !== 'mchat' && (
                              <span className="ml-auto text-xs text-gray-500">
                                <i className="fas fa-lock mr-1"></i>
                                Sign In
                              </span>
                            )}
                          </button>
                        ))}
                      </div>
                    </div>
                    
                    {/* Popular searches section removed as per user request */}
                    
                    {/* Current Search Suggestions */}
                    {searchQuery && (
                      <div className="p-3 bg-gray-50 border-t border-gray-100">
                        <div className="space-y-1">
                          <button
                            onClick={(e) => handleSearch(e, searchQuery, 'therapist')}
                            className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-white rounded-lg transition-colors flex items-center"
                          >
                            <i className="fas fa-search text-gray-400 mr-3 w-5"></i>
                            <span>Search for "{searchQuery}" in Therapists</span>
                          </button>
                          <button
                            onClick={(e) => handleSearch(e, searchQuery, 'resource')}
                            className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-white rounded-lg transition-colors flex items-center"
                          >
                            <i className="fas fa-book text-gray-400 mr-3 w-5"></i>
                            <span>Search for "{searchQuery}" in Resources</span>
                          </button>
                          <button
                            onClick={(e) => handleSearch(e, searchQuery, 'article')}
                            className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-white rounded-lg transition-colors flex items-center"
                          >
                            <i className="fas fa-newspaper text-gray-400 mr-3 w-5"></i>
                            <span>Search for "{searchQuery}" in Articles</span>
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
              <button type="submit" className="absolute right-3 top-1/2 transform -translate-y-1/2">
                <FaSearch className="h-4 w-4 text-gray-400" />
              </button>
            </form>
          </div>
          {/* Desktop Navigation */}
          <nav className="flex items-center gap-2 md:gap-4">


            {user ? (
              <>
                {(userRole && normalizeRole(userRole, user?.email) === 'admin') && (
                  <button 
                    onClick={() => {
                      navigate('/admin');
                    }}
                    className="hidden md:inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 hover:text-blue-600 transition-colors"
                  >
                    Admin
                  </button>
                )}
                <button 
                  onClick={handleDashboardNav} 
                  className="hidden md:flex items-center px-4 py-2 text-sm font-medium text-gray-700 hover:text-blue-600 transition-colors"
                >
                  Dashboard
                </button>
                <button 
                  onClick={handleProfileNav}
                  className="hidden md:flex items-center px-4 py-2 text-sm font-medium text-gray-700 hover:text-blue-600 transition-colors"
                >
                  My Profile
                </button>
                <a 
                  href="/app/report"
                  className="hidden md:flex items-center px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700 transition-colors font-semibold"
                >
                  Assessments
                </a>
                <div className="relative group" onMouseEnter={() => setShowMobileNav(false)}>
                  <div className="relative">
                    <button 
                      onClick={handleProfileNav}
                      className="flex flex-col items-center justify-center w-12 h-12 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors group-hover:bg-gray-200"
                      aria-label="User profile"
                      aria-expanded={showMobileNav ? 'true' : 'false'}
                      onMouseEnter={(e) => {
                        e.stopPropagation();
                        document.querySelector('.profile-dropdown')?.classList.remove('hidden');
                      }}
                    >
                      {user.photoURL ? (
                        <img 
                          src={user.photoURL} 
                          alt="Profile" 
                          className="w-10 h-10 rounded-full object-cover border-2 border-white shadow-sm" 
                        />
                      ) : (
                        <FaUserCircle className="w-10 h-10 text-gray-400" />
                      )}
                      <span className="text-xs text-gray-600 mt-1 truncate w-16 text-center">
                        {user.displayName?.split(' ')[0] || user.email?.split('@')[0] || 'Profile'}
                      </span>
                    </button>
                    <div 
                      className="profile-dropdown absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-50 hidden"
                      onMouseLeave={() => {
                        setTimeout(() => {
                          const dropdown = document.querySelector('.profile-dropdown');
                          if (dropdown && !dropdown.matches(':hover')) {
                            dropdown.classList.add('hidden');
                          }
                        }, 300);
                      }}
                    >
                      <button 
                        onClick={handleSignOut}
                        className="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                      >
                        Sign Out
                      </button>
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <button 
                onClick={() => window.showSignupModal?.()} 
                className="px-5 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white text-sm font-medium rounded-full shadow-sm hover:shadow-md transition-all duration-200"
              >
                Sign In
              </button>
            )}
            
            {/* Mobile menu button */}
            <button 
              className="lg:hidden ml-2 p-2 rounded-md text-gray-700 hover:bg-gray-100 focus:outline-none" 
              onClick={() => setShowMobileNav(!showMobileNav)}
              aria-label="Toggle navigation"
            >
              {showMobileNav ? <FiX size={24} /> : <FiMenu size={24} />}
            </button>
          </nav>
        </div>

        {/* Mobile header */}
        <div className="md:hidden fixed top-0 left-0 right-0 bg-white/95 backdrop-blur-md border-b border-gray-100 shadow-sm z-40 px-4 py-2">
          <div className="flex items-center justify-between w-full">
            {/* Logo - Left side */}
            <button 
              onClick={() => navigate('/')} 
              className="flex-shrink-0 p-2 -ml-2"
              aria-label="Home"
            >
              <LogoWithSlogan />
            </button>
            
            {/* Hamburger Menu Button - Right aligned */}
            <button
              onClick={() => setShowMobileNav(!showMobileNav)}
              className="text-gray-600 hover:text-gray-900 focus:outline-none"
              aria-label="Toggle menu"
            >
              {showMobileNav ? <FiX size={24} /> : <FiMenu size={24} />}
            </button>
          </div>

          {/* Mobile Menu */}
          {showMobileNav && (
            <div 
              className="fixed inset-0 bg-black/50 z-40"
              onClick={() => setShowMobileNav(false)}
              style={{ zIndex: 49 }}
            />
          )}
          {showMobileNav && (
            <div 
              className="lg:hidden bg-white border-t border-gray-100 shadow-lg fixed top-16 left-0 right-0 z-50"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="px-4 py-3 space-y-1">

                
                {user ? (
                  <>
                    {(userRole && normalizeRole(userRole, user?.email) === 'admin') && (
                      <button 
                        onClick={() => {
                          setShowMobileNav(false);
                          navigate('/admin');
                        }}
                        className="block w-full text-left px-3 py-2 text-base font-medium text-gray-700 hover:bg-gray-50 rounded-md"
                      >
                        Admin
                      </button>
                    )}
                    <button 
                      onClick={handleDashboardNav} 
                      className="block w-full text-left px-3 py-2 text-base font-medium text-gray-700 hover:bg-gray-50 rounded-md"
                    >
                      Dashboard
                    </button>
                    <button 
                      onClick={handleProfileNav}
                      className="block w-full text-left px-3 py-2 text-base font-medium text-gray-700 hover:bg-gray-50 rounded-md"
                    >
                      My Profile
                    </button>
                    <a 
                      href="/app/report"
                      className="block w-full text-left px-3 py-2 text-base font-medium text-blue-600 hover:bg-blue-50 rounded-md font-semibold"
                      onClick={() => setShowMobileNav(false)}
                    >
                      Assessments
                    </a>
                    <button 
                      onClick={handleSignOut}
                      className="block w-full text-left px-3 py-2 text-base font-medium text-red-600 hover:bg-red-50 rounded-md"
                    >
                      Sign Out
                    </button>
                  </>
                ) : (
                  <button
                    onClick={() => window.showSignupModal?.()}
                    className="block w-full text-left px-3 py-2 text-base font-medium text-blue-600 hover:bg-blue-50 rounded-md"
                  >
                    Sign In
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </header>
      <SignupModal 
        show={showSignup} 
        onClose={() => setShowSignup(false)}
        onSuccess={handleSignupSuccess}
      />
    </>
  );
}

Header.propTypes = {
  adminMode: PropTypes.bool
};
